import React, { useEffect, useState } from "react";
import styles from "./index.module.css";
import {
  Button,
  CircularProgress,
  createStyles,
  FormControl,
  Grid,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Typography,
  withStyles,
} from "@material-ui/core";
import { Field, FieldProps, Form, Formik } from "formik";
import { useSelector } from "react-redux";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useHistory } from "react-router-dom";
import { DateTime as d } from "luxon";
import HttpHeader from '../../../../header';
import * as UUID from "uuid";
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import FlatPickerBar from '../../../FlatPicker';
import { IAppState } from '../../../../store';
import { useSnackBar } from '../../../SnackBarContext/SnackBarContext';
import { SnackBarVariant } from '../../../SnackbarWrapper/SnackbarWrapper';
import { buildFieldRequiredMap, buildFieldVisibilityMap, DATE_TYPE, defaultWebsiteIndividualFields, formatGraphQLErrorMessage, getUploadedImageByKey, returnfileContentTypeAndExtention } from '../../../utils';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import * as Yup from "yup"
import { ADD_DRIVER_MUTATION } from '../../../../graphql/user/updateUser';
import { getDefaultCountryCode, getLocalisedLicense, getLocalizedDateFormat } from "../../../../utils/localized.syntex";
import AddressPicker from "../../../AddressPicker/AddressPicker";
import { IAddress } from "../../../../reducers/bookings/types";
import { useTranslation } from "react-i18next";
import { GET_CUSTOM_FORM_BY_TYPE } from "../../../../graphql/customers/getConsumerCustomForm";
import { TranslationKeys } from "../../../../utils/translations/types";
import { Gender } from "../../../../reducers/drivers/const";
import { getCountryFromLocaleString } from "../../../Profile";

const StyledInput = withStyles(() =>
  createStyles({
    root: {
      border: "1px solid #c0c0c0",
      borderRadius: 6,
      padding: "8px 10px",
      marginTop: 5,
      '&.Mui-focused': {
        borderColor: "var(--primary-color)"
      },
      '&.Mui-error': {
        borderColor: "#D13135",
        color: "#D13135"
      }
    },
  }),
)(InputBase);
interface ILicenseDocument {
  documentKey?: string;
  document?: string;
}
interface IProps {

}

const licenseImageInitial: ILicenseDocument[] = [{
  documentKey: "",
  document: ""
}, {
  documentKey: "",
  document: ""
}]

const AddDriver: React.FC<IProps> = () => {
  const snackbar = useSnackBar();
  const history = useHistory();
  const website = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite
  );
  const { country } = website.organisation.address;
  const user = useSelector(
    (state: IAppState) => state.authReducer.user
  );
  const organisationData = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation);
  const { _e_ } = useSelector((state: IAppState) => state.authReducer);
  const [licenseImages, setLicenseImages] = useState<ILicenseDocument[]>(licenseImageInitial);
  const [isLocation, setIsLocation] = useState<boolean>(false);
  const { t } = useTranslation();
  const [requiredMap, setRequiredMap] = useState<Record<string, boolean>>(
    buildFieldRequiredMap(defaultWebsiteIndividualFields)
  );

  const [getCustomFormByType] = useLazyQuery(GET_CUSTOM_FORM_BY_TYPE, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {

      if (data?.getConsumerCustomFormType?.config?.website?.fields.length) {
        const customMap = buildFieldRequiredMap(data.getConsumerCustomFormType.config.website.fields);
        setRequiredMap(customMap);
      } else {
        const defaultMap = buildFieldRequiredMap(defaultWebsiteIndividualFields);
        setRequiredMap(defaultMap);
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      const defaultMap = buildFieldRequiredMap(defaultWebsiteIndividualFields);
      setRequiredMap(defaultMap);
    }
  });

  useEffect(() => {
    getCustomFormByType({
      variables: {
        organisationId: organisationData.id,
        formType: "INDIVIDUAL",
        atBranchLevel: false
      }
    })
  }, [])

  const [addDriverMutation, { loading: addDriverLoading }] = useMutation(ADD_DRIVER_MUTATION, {
    fetchPolicy: "no-cache",
    onCompleted: ({ addBusinessCustomerDriver }) => {
      snackbar({
        message: t("snackBar:driverAdded"),
        variant: SnackBarVariant.SUCCESS
      });
      window.location.replace("/account/drivers")
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      })
  });

  const uploadLicenceImage = async (
    e: any,
    index: number
  ) => {
    try {
      const profilePicArr: any[] = [];
      const showUploadedImages: any[] = [];
      var filesArr = Array.prototype.slice.call(e.target.files);
      const file = filesArr[0];
      if (file.size > 52428800) {
        throw new Error(t("snackBar:fileSizeExceeded50mb"));
      }
      if (file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/jpg") {
        const { fileExtension, contentType } = returnfileContentTypeAndExtention(file);
        const uniqueId = UUID.v4();
        const key = `${uniqueId}.${fileExtension}`;
        if (_e_) {
          await _e_
            .add({
              name: key,
              file: file,
              complete: async () => {
                profilePicArr.push(key);
                snackbar({
                  message: `${getLocalisedLicense(country)} ${t("snackBar:ImageUploaded")}`,
                  variant: SnackBarVariant.SUCCESS
                });
                const uploadedFile = await getUploadedImageByKey(key);
                if (uploadedFile) {
                  showUploadedImages.push(uploadedFile);
                }
                const _images = [...licenseImages];
                _images[index] = {
                  documentKey: key,
                  document: uploadedFile
                }
                setLicenseImages(_images)
              }
            });
        }
      } else {
        return snackbar({
          message: t("snackBar:uploadImageFormatError"),
          variant: SnackBarVariant.ERROR
        });
      }
    } catch (err: any) {
      snackbar({
        message: err.message,
        variant: SnackBarVariant.ERROR
      });
    }
  };

  const createDynamicApprovedDriverSchema = (
    requiredMap: Record<string, boolean>,
    t: (key: string, options?: Record<string, any>) => string
  ) => {
    const getLabel = (labelKey: string) => t(labelKey) ?? labelKey;

    const createConditionalString = (
      key: string,
      errorKey: TranslationKeys,
      interpolationValues?: Record<string, string | number>
    ) =>
      requiredMap[key]
        ? interpolationValues
          ? Yup.string().required(t(errorKey, { ...interpolationValues }))
          : Yup.string().required(t(errorKey))
        : Yup.string();

    const createConditionalEmail = (fieldKey: string, labelKey: string) => {
      const base = Yup.string().email(
        t("validation:pleaseEnterAValidEmail", { field: getLabel(labelKey) })
      );
      return requiredMap[fieldKey]
        ? base.required(
          t("validation:emailIsRequired", { field: getLabel(labelKey) })
        )
        : base;
    };

    const createConditionalPhone = (fieldKey: string, labelKey: string) => {
      const base = Yup.string().test(
        "is-valid-phone",
        t("root:invalidPhoneNumber"),
        (value) => {
          if (!value) return true;
          return isValidPhoneNumber(value);
        }
      );

      return requiredMap[fieldKey]
        ? base.required(
          t("validation:phoneNumberRequired", {
            field: getLabel(labelKey),
          })
        )
        : base;
    };

    const createAddressSchema = (fieldKey: string, labelKey: string) => {
      if (!requiredMap[fieldKey]) {
        return Yup.object().shape({
          fullAddress: Yup.string(),
          country: Yup.string(),
          state: Yup.string(),
          city: Yup.string(),
          street: Yup.string(),
          zipcode: Yup.string(),
        });
      }

      return Yup.object()
        .shape({
          fullAddress: Yup.string().required(
            t("validation:required", { field: t("root:address") })
          ),
          country: Yup.string().required(
            t("validation:required", { field: t("root:country") })
          ),
          state: Yup.string().required(
            t("validation:required", { field: t("root:state") })
          ),
          city: Yup.string().required(
            t("validation:required", { field: t("root:city") })
          ),
          street: Yup.string().required(
            t("validation:required", { field: t("root:street") })
          ),
          zipcode: Yup.string(),
        })
        .required(t("validation:required", { field: getLabel(labelKey) }));
    };

    return Yup.object().shape({
      firstName: createConditionalString(
        "firstName",
        "validation:firstNameRequired"
      ),
      lastName: createConditionalString(
        "lastName",
        "validation:lastNameRequired"
      ),

      email: createConditionalEmail("email", "root:emailAddress"),

      secondaryEmail: createConditionalEmail(
        "secondaryEmail",
        "root:secondaryEmailAddress"
      ),

      phoneNumber: Yup.object().shape({
        phone: createConditionalPhone("phoneNumber", "root:phoneNumber"),
        country: Yup.string(),
      }),

      secondaryPhoneNumber: Yup.object().shape({
        phone: createConditionalPhone(
          "secondaryPhoneNumber",
          "root:secondaryPhoneNumber"
        ),
        country: Yup.string(),
      }),

      location: createAddressSchema("location", "root:location"),

      nationalInsuranceNumber: createConditionalString(
        "nationalInsuranceNumber",
        "validation:nationalInsuranceNumberRequired"
      ),

      profession: createConditionalString(
        "profession",
        "validation:professionIsRequired"
      ),

      dateOfBirth: createConditionalString(
        "dateOfBirth",
        "root:dateOfBirth"
      ),

      gender: createConditionalString("gender", "validation:genderRequired"),

      customerPCONumber: createConditionalString(
        "customerPCONumber",
        "validation:customerPCONumberRequired"
      ),

      accountNumber: createConditionalString(
        "accountNumber",
        "validation:accountNumberRequired"
      ),

      license: Yup.object().shape({
        licenseNumber: createConditionalString(
          "licenseNumber",
          "validation:licenceNumberRequired",
          { licenceSyntax: getLocalisedLicense(country) }
        ),
        authority: createConditionalString(
          "authority",
          "validation:authorityRequired"
        ),
        dateOfIssue: createConditionalString(
          "dateOfIssue",
          "root:drivingLicenceIssueDate"
        ),
      }),
    });
  };

  const approvedDriverSchema = createDynamicApprovedDriverSchema(requiredMap, t);

  const ninetyFiveYearsOldDate = getLocalizedDateFormat(
    country,
    d.now().minus({ year: 95 }).toUTC().toISO(),
    DATE_TYPE.CONDENSED
  );
  const eighteenYearsOldDate = getLocalizedDateFormat(
    country,
    d.now().minus({ year: 18 }).toUTC().toISO(),
    DATE_TYPE.CONDENSED
  );

  if (!user || !user.businesscustomers) {
    return <></>
  }

  const secondaryEmailAddressPlaceholder = requiredMap.secondaryEmail
    ? `${t("root:secondaryEmailAddress")} *`
    : t("root:secondaryEmailAddress");

  const secondaryPhoneNumberPlaceholder = requiredMap.secondaryPhoneNumber
    ? `${t("root:secondaryPhoneNumber")} *`
    : t("root:secondaryPhoneNumber");

  const customerPCONumberPlaceholder = requiredMap.customerPCONumber
    ? `${t("root:pcoPrivateHiresHireDLNo")} *`
    : t("root:pcoPrivateHiresHireDLNo");

  const accountNumberPlaceholder = requiredMap.accountNumber
    ? `${t("root:accountNumber")} *`
    : t("root:accountNumber");

  const genderPlaceholder = requiredMap.gender
    ? `${t("root:gender")} *`
    : t("root:gender");


  return (
    <div className={styles.column}>
      <div className="flex cross-center margin-bottom padding-bottom">
        <div className="flex cross-center">
          <IconButton
            size="small"
            edge="start"
            onClick={() => history.goBack()}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="semi-bold">{t("root:addNewDriver")}</Typography>
        </div>
        <span
          className="flex fill margin-left"
          style={{ borderBottom: "1px solid #e9e9e9", marginTop: 4 }}
        ></span>
      </div>
      <Formik
        initialValues={{
          nationalInsuranceNumber: "",
          profession: "",
          dateOfBirth: "",
          firstName: "",
          lastName: "",
          phoneNumber: {
            phone: "",
            country: ""
          },
          email: "",
          gender: "",
          accountNumber: "",
          secondaryEmail: "",
          secondaryPhoneNumber: {
            phone: "",
            country: ""
          },
          customerPCONumber: "",
          license: {
            authority: "",
            dateOfExpiry: "",
            dateOfIssue: "",
            licenseNumber: "",
          },
          location: {
            city: "",
            country: "",
            fullAddress: "",
            state: "",
            street: "",
            zipcode: ""
          },
        }}
        validationSchema={approvedDriverSchema}
        onSubmit={(values) => {
          if (values.location && values.location.fullAddress) {
            if (!(values.location.street || values.location.zipcode)) {
              return snackbar({
                message: t("snackBar:streetZipcodeRequired"),
                variant: SnackBarVariant.ERROR
              });
            }
          }

          if (requiredMap.dateOfExpiry && !values.license?.dateOfExpiry) {
            snackbar({
              message: t('validation:required', {
                field: t('root:drivingLicenceExpiryDate', {
                  licenceSyntax: getLocalisedLicense(country),
                }),
              }),
              variant: SnackBarVariant.ERROR,
            });
            return;
          }

          if (requiredMap.dateOfBirth && !values.dateOfBirth) {
            snackbar({
              message: t('validation:required', { field: t('root:dateOfBirth') }),
              variant: SnackBarVariant.ERROR,
            });
            return;
          }

          if (requiredMap.dateOfIssue && !values.license?.dateOfIssue) {
            snackbar({
              message: t('validation:required', { field: t('root:drivingLicenceIssueDate') }),
              variant: SnackBarVariant.ERROR,
            });
            return;
          }

          addDriverMutation({
            variables: {
              driver: {
                ...values,
                license: {
                  ...values.license,
                  images: licenseImages.map(im => im.documentKey)
                }
              },
              businessCustomerId: user?.businesscustomers?.id
            }
          })
        }}
      >
        {(formikProps) => (
          <Form className={styles.profile}>
            <div className={styles.card}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small">
                    {t("root:firstName")}
                    {requiredMap.firstName && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <Field name="firstName">
                    {({ field, meta: { touched, error }, }: FieldProps) => (
                      <>
                        <StyledInput
                          {...field}
                          placeholder={t("root:enterFirstName")}
                          fullWidth
                          error={touched && Boolean(error)}
                        />
                        <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small">
                    {t("root:lastName")}
                    {requiredMap.lastName && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <Field name="lastName">
                    {({ field, meta: { touched, error }, }: FieldProps) => (
                      <>
                        <StyledInput
                          {...field}
                          placeholder={t("root:enterLastName")}
                          fullWidth
                          error={touched && Boolean(error)}
                        />
                        <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="margin-bottom">
                    <Typography variant="body1" className="opacity60 padding-bottom--small" >
                      {t("root:email")}
                      {requiredMap.email && <span style={{ color: "red" }}> *</span>}
                    </Typography>
                    <Field name="email">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={t("root:enterYourMail")}
                            fullWidth
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </div>
                </Grid>
                <Grid xs={12} md={6} item>
                  <span className="font-medium opacity50">
                    {t("root:phoneNo")}
                    {requiredMap.phoneNumber && <span style={{ color: "red" }}> *</span>}
                  </span>
                  <Field
                    component={StyledInput}
                    name={"phoneNumber.phone"}
                    required={requiredMap.phoneNumber}
                  >
                    {({ field, meta: { touched, error } }: FieldProps) => {
                      return (
                        <div className={`phone-input-container`} style={{ marginTop: 9 }}>
                          <div style={{ border: `1px solid ${(touched && Boolean(error)) ? '#D13135' : '#c0c0c0'}`, borderRadius: 6 }}>
                            <PhoneInput
                              {...field}
                              international={false}
                              defaultCountry={getDefaultCountryCode(website.organisation.address.country)}   //
                              placeholder={`${t("root:phonePlaceholder")}`}
                              className={(touched && Boolean(error)) ? "PhoneInputInputError" : ""}
                              name={"phoneNumber.phone"}
                              required={requiredMap.phoneNumber}
                              onChange={(val) => {
                                formikProps.setFieldValue('phoneNumber.phone', val)
                              }}
                              onCountryChange={(val) => {
                                formikProps.setFieldValue('phoneNumber.country', val)
                              }}
                            />
                          </div>
                          <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                        </div>
                      );
                    }}
                  </Field>
                </Grid>
                <Grid xs={12} md={6} item>
                  <AddressPicker
                    fieldName={"location"}
                    required={isLocation || requiredMap.location}
                    onChange={(address: IAddress) => {
                      if (address && address.fullAddress) {
                        setIsLocation(true)
                        formikProps.setFieldValue('location', address)
                      }
                      else {
                        formikProps.setFieldValue("location", {
                          street: "",
                          city: "",
                          country: "",
                          fullAddress: "",
                          state: "",
                          zipcode: ""
                        })
                        setIsLocation(false)
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </div>
            <div className={styles.card}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small">
                    {secondaryEmailAddressPlaceholder}
                  </Typography>

                  <Field name="secondaryEmail">
                    {({ field, meta: { touched, error } }: FieldProps) => (
                      <>
                        <StyledInput
                          {...field}
                          placeholder={secondaryEmailAddressPlaceholder}
                          fullWidth
                          error={touched && Boolean(error)}
                        />
                        <Typography variant="body2" color="error">
                          {touched && Boolean(error) && error}
                        </Typography>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small">
                    {genderPlaceholder}
                  </Typography>

                  <FormControl variant="outlined" fullWidth>
                    <Select
                      value={formikProps.values.gender || ""}
                      onChange={(event) =>
                        formikProps.setFieldValue("gender", event.target.value)
                      }
                      displayEmpty
                    >
                      {Object.values(Gender).map((element: string, key: number) => (
                        <MenuItem value={element} key={key}>
                          {element.replace(/_/g, " ")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small">
                    {secondaryPhoneNumberPlaceholder}
                  </Typography>

                  <Field name="secondaryPhoneNumber.phone">
                    {({ field, meta: { touched, error } }: FieldProps) => (
                      <div className="phone-input-container" style={{ marginTop: 5 }}>
                        <div
                          style={{
                            border: `1px solid ${touched && Boolean(error) ? "#D13135" : "#c0c0c0"
                              }`,
                            borderRadius: 6,
                          }}
                        >
                          <PhoneInput
                            {...field}
                            international={false}
                            defaultCountry={getCountryFromLocaleString(
                              website.organisation.locale
                            )}
                            onChange={(val: any) =>
                              formikProps.setFieldValue(
                                "secondaryPhoneNumber.phone",
                                val
                              )
                            }
                          />
                        </div>

                        <Typography variant="body2" color="error">
                          {touched && Boolean(error) && error}
                        </Typography>
                      </div>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {customerPCONumberPlaceholder}
                  </Typography>

                  <Field name="customerPCONumber">
                    {({ field, meta: { touched, error } }: FieldProps) => (
                      <>
                        <StyledInput
                          {...field}
                          placeholder={t("root:privateHiresNo")}
                          fullWidth
                          inputProps={{ maxLength: 10 }}
                          error={touched && Boolean(error)}
                        />
                        <Typography variant="body2" color="error">
                          {touched && Boolean(error) && error}
                        </Typography>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small">
                    {accountNumberPlaceholder}
                  </Typography>
                  <Field name="accountNumber">
                    {({ field, meta: { touched, error }, }: FieldProps) => (
                      <>
                        <StyledInput
                          {...field}
                          placeholder={accountNumberPlaceholder}
                          fullWidth
                          error={touched && Boolean(error)}
                        />
                        <Typography variant="body1" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small">
                    {t("root:nationalInsuranceNumber")}
                    {requiredMap.nationalInsuranceNumber && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <Field name="nationalInsuranceNumber">
                    {({ field, meta: { touched, error }, }: FieldProps) => (
                      <>
                        <StyledInput
                          {...field}
                          placeholder={t('root:enterNationalInsuranceNumber')}
                          fullWidth
                          error={touched && Boolean(error)}
                        />
                        <Typography variant="body1" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small">
                    {t('root:profession')}
                    {requiredMap.profession && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <Field name="profession">
                    {({ field, meta: { touched, error }, }: FieldProps) => (
                      <>
                        <StyledInput
                          {...field}
                          placeholder={t('root:enterYourProfession')}
                          fullWidth
                          error={touched && Boolean(error)}
                        />
                        <Typography variant="body1" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small" >
                    {t("root:drivingLicenceNumber")}
                    {requiredMap.licenseNumber && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <Field name="license.licenseNumber">
                    {({ field, meta: { touched, error }, }: FieldProps) => (
                      <>
                        <StyledInput
                          {...field}
                          placeholder={t("root:enterDrivingLicenceNumber")}
                          fullWidth
                          error={touched && Boolean(error)}
                        />
                        <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small" >
                    {t('root:licenceIssuedBy', { licenceSyntax: getLocalisedLicense(country) })}
                    {requiredMap.authority && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <Field name="license.authority">
                    {({ field, meta: { touched, error }, }: FieldProps) => (
                      <>
                        <StyledInput
                          {...field}
                          placeholder={t('root:licenceIssuedBy', { licenceSyntax: getLocalisedLicense(country) })}
                          fullWidth
                          error={touched && Boolean(error)}
                        />
                        <Typography variant="body1" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small" >
                    {t('root:drivingLicenceIssueDate')}
                    {requiredMap.dateOfIssue && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <FlatPickerBar
                    enableTime={false}
                    handleDateChange={(value: Date) => {
                      formikProps.setFieldValue("license.dateOfIssue", value ? d.fromJSDate(value).toUTC().toISO() : "");
                    }}
                    label={""}
                    identifier={"dateOfIssue"}
                    placeholderValue={t("root:drivingLicenceIssueDate")}
                    value={formikProps.values.license?.dateOfIssue ? getLocalizedDateFormat(country, formikProps.values.license.dateOfIssue, DATE_TYPE.CONDENSED) : ""}
                    maxDate={getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED)}
                    country={country}
                  />
                  <Typography variant="body2" color="error" className={styles.inputError}>
                    {formikProps.touched.license?.dateOfIssue && formikProps.errors.license?.dateOfIssue}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small" >
                    {t('root:dateOfBirth')}
                    {requiredMap.dateOfBirth && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <FlatPickerBar
                    enableTime={false}
                    handleDateChange={(value: Date) => {
                      formikProps.setFieldValue("dateOfBirth", value ? d.fromJSDate(value).startOf("day").toUTC().toISO() : "");
                    }}
                    label={""}
                    identifier={"dateOfBirth"}
                    placeholderValue={t("root:dateOfBirth")}
                    value={formikProps.values.dateOfBirth ? getLocalizedDateFormat(country, formikProps.values.dateOfBirth, DATE_TYPE.CONDENSED) : ""}
                    minDate={ninetyFiveYearsOldDate}
                    maxDate={eighteenYearsOldDate}
                    country={country}
                  />
                  <Typography variant="body2" color="error" className={styles.inputError}>
                    {formikProps.touched.dateOfBirth && formikProps.errors.dateOfBirth}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small" > {t("root:drivingLicenceImage")} </Typography>
                  <div className="flex">
                    {licenseImages.map((image, index) => {
                      return (
                        <div key={index} className={styles.dlImage}>
                          {image.documentKey ? <div
                            style={{
                              border: "none",
                              boxShadow: "none",
                              margin: 0,
                              padding: 0,
                              height: 56,
                              width: "100%",
                            }}
                          >
                            <img src={image.document} alt="licance" />
                          </div> : <>
                            <label htmlFor={`LICENCE_IMG_${index}`} style={{ cursor: "pointer" }}>
                              <Typography variant="body2">{t("root:upload")} {index ? t("misc:back") : t("misc:front")}</Typography>
                            </label>
                            <input accept="image/*" style={{ display: 'none' }} onChange={(e: any) => { uploadLicenceImage(e, index); e.target.value = null }} id={`LICENCE_IMG_${index}`} type="file" />
                          </>
                          }
                        </div>
                      );
                    })
                    }
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="opacity60 padding-bottom--small" >
                    {t('root:licenceExpiryDate', { licenceSyntax: getLocalisedLicense(country) })}
                    {requiredMap.dateOfExpiry && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <FlatPickerBar
                    enableTime={false}
                    handleDateChange={(value: Date) => {
                      formikProps.setFieldValue("license.dateOfExpiry", d.fromJSDate(value).endOf("day").toUTC().toISO())
                    }}
                    label={""}
                    identifier={"dateOfExpiry"}
                    placeholderValue={t("root:drivingLicenceExpiryDate")}
                    value={formikProps.values.license.dateOfExpiry ? getLocalizedDateFormat(country, formikProps.values.license.dateOfExpiry, DATE_TYPE.CONDENSED) : ""}
                    minDate={d.now().startOf("day").toUTC().toISO()}
                    country={country}
                  />
                </Grid>
              </Grid>
            </div>
            <div style={{ marginTop: 30 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disableRipple
                disabled={addDriverLoading}
              >{addDriverLoading && <CircularProgress size={16} style={{ color: "black", marginRight: "10px" }} />}
                {t("misc:save")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddDriver;
